import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Target, X, Trophy, Calendar, AlarmClock, TrendingUp, Package } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { dateStyle: "medium" }); } catch { return s; }
}

function periodToEndDate(period) {
  // period is "YYYY-MM" — return last day of that month
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return null;
  const [y, m] = period.split("-").map(Number);
  return new Date(y, m, 0); // day 0 of next month = last day of current
}

/**
 * <TargetDetailDialog> — opens on a target card click. Pulls extra context:
 * - duration (period start/end, days remaining)
 * - product/unit + achievement %
 * - top employee for the same target_type+period (so the salesman can see who's leading)
 * - deadline + a "reminder schedule" derived from days_remaining
 */
export function TargetDetailDialog({ open, onClose, target, employees }) {
  const [topPeer, setTopPeer] = useState(null);

  useEffect(() => {
    if (!open || !target) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/targets?target_type=${encodeURIComponent(target.target_type)}&period=${encodeURIComponent(target.period)}`);
        const j = await res.json();
        if (!cancelled && j.success) {
          const sorted = (j.data || []).slice().sort((a, b) => (b.achievement_pct || 0) - (a.achievement_pct || 0));
          setTopPeer(sorted[0] || null);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, target]);

  if (!target) return null;

  const pct = target.achievement_pct || 0;
  const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-blue-500" : "bg-amber-500";
  const endDate = periodToEndDate(target.period);
  const daysRemaining = endDate ? Math.ceil((endDate.getTime() - Date.now()) / 86400000) : null;
  const employee = employees?.find?.((e) => e.employee_id === target.employee_id);

  // Reminder schedule heuristic: a salesman gets pinged at T-7d, T-3d, T-1d, T-0
  const reminders = [];
  if (endDate && daysRemaining !== null && target.status === "ACTIVE") {
    const points = [7, 3, 1, 0];
    for (const d of points) {
      const fire = new Date(endDate);
      fire.setDate(fire.getDate() - d);
      reminders.push({
        label: d === 0 ? "On deadline" : `T-${d} days`,
        at: fire,
        fired: fire.getTime() <= Date.now(),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="target-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-[#8B1A1A]" />
            {target.target_type} Target · {target.period}
            <Badge className={`ml-2 text-xs ${pct >= 100 ? "bg-emerald-100 text-emerald-700" : pct >= 50 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
              {pct}% achieved
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Employee</p>
              <p className="text-sm font-medium mt-0.5">{employee?.full_name || target.employee_name || target.employee_id || "—"}</p>
              {(employee?.employee_code || target.employee_code) && (
                <p className="text-[10px] text-slate-400">{employee?.employee_code || target.employee_code}</p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Package className="w-3 h-3" /> Product / Unit</p>
              <p className="text-sm font-medium mt-0.5">{target.product_id || target.product || target.unit || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Duration</p>
              <p className="text-sm font-medium mt-0.5">{target.period}</p>
              <p className="text-[10px] text-slate-400">Ends {fmtDate(endDate)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><AlarmClock className="w-3 h-3" /> Deadline</p>
              <p className={`text-sm font-medium mt-0.5 ${daysRemaining !== null && daysRemaining < 0 ? "text-red-600" : daysRemaining !== null && daysRemaining <= 3 ? "text-amber-600" : ""}`}>
                {daysRemaining === null ? "—" : daysRemaining < 0 ? `${Math.abs(daysRemaining)}d ago` : daysRemaining === 0 ? "Today" : `${daysRemaining}d left`}
              </p>
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Progress</p>
              <p className="text-xs text-slate-500 font-mono">
                <span className="font-bold text-slate-900 dark:text-white">{target.achieved_value}</span>
                <span className="text-slate-400"> / {target.target_value} {target.unit}</span>
              </p>
            </div>
            <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} data-testid="target-progress-bar" />
            </div>
            {target.description && (
              <p className="text-xs text-slate-500 mt-2 italic">{target.description}</p>
            )}
          </div>

          {/* Top performer for same target_type+period */}
          {topPeer && topPeer.target_id !== target.target_id && (
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-900/30 flex items-center gap-3" data-testid="target-top-employee">
              <Trophy className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-700 dark:text-amber-300 font-semibold">Top performer · {target.target_type} · {target.period}</p>
                <p className="text-sm font-bold text-amber-900 dark:text-amber-100 truncate">
                  {topPeer.employee_name || topPeer.employee_id}
                  <span className="ml-2 text-amber-700 font-normal">— {topPeer.achievement_pct}% ({topPeer.achieved_value} / {topPeer.target_value} {topPeer.unit})</span>
                </p>
              </div>
            </div>
          )}
          {topPeer && topPeer.target_id === target.target_id && (
            <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-900/30 flex items-center gap-3" data-testid="target-top-self">
              <Trophy className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold">You're leading this {target.target_type} target for {target.period}!</p>
                <p className="text-sm text-emerald-900 dark:text-emerald-100">Keep it up — next milestone at {Math.min(100, Math.ceil(pct / 10) * 10 + 10)}%.</p>
              </div>
            </div>
          )}

          {/* Reminder schedule */}
          {reminders.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1">
                <AlarmClock className="w-3.5 h-3.5 text-slate-400" /> Reminder schedule
              </p>
              <div className="space-y-1.5" data-testid="target-reminders">
                {reminders.map((r, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${r.fired ? "bg-slate-100 dark:bg-slate-800 text-slate-500" : "bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"}`}>
                    <span>{r.label}</span>
                    <span className="font-mono">{fmtDate(r.at)} {r.fired ? "· sent" : "· upcoming"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} className="bg-white border-2 border-slate-300 text-slate-700">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TargetDetailDialog;
